import React from 'react';

/**
 * Eylül 2026 medya planı sunum kiti.
 *
 * Dört marka (HUPALUPA Store, HUPALUPA Eğlence, Kixx, Keramet Ilıcası) için
 * ÜÇER slaytlık sunumlar bu kiti paylaşır. Ağustos sayfalarından farkı: bütçe
 * müşteriden gelmedi, biz öneriyoruz — o yüzden her sunum aynı üç adımı izler:
 *   1) Talep  — Eylül'de kategoride ne oluyor (Keyword Planner, Ağu→Eyl, iki yıl)
 *   2) Kanıt  — markanın KENDİ Temmuz/Ağustos gerçekleşen verisi
 *   3) Talep  — önerilen bütçe, dağılım, beklenen sonuç ve şartlar
 *
 * 🔑 Slaytlarda görünen her sayı ya Keyword Planner'dan ya canlı mecra API'sinden
 * geldi; hiçbiri benchmark değil. Kelime hacimlerinin yanına KELİMEYİ de yazın —
 * kelimesiz hacim toplantıda savunulamaz.
 */

export function Deck({ marka, altBaslik, logo, logoBg, donem = 'Eylül 2026', butce, oncekiButce, children }) {
  const fark = butce && oncekiButce ? (butce - oncekiButce) / oncekiButce : null;
  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-6 print:p-0 print:space-y-0">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between print:hidden">
        <div className="flex items-center gap-4">
          {logo && (
            <div className={`rounded-lg px-3 py-2 ${logoBg || 'bg-gray-100'}`}>
              <img src={logo} alt={marka} className="h-7 w-auto object-contain" />
            </div>
          )}
          <div>
            <h1 className="text-lg font-bold text-gray-800">{marka} — {donem} Medya Planı</h1>
            <p className="text-sm text-gray-500">{altBaslik}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {butce != null && (
            <span className="inline-flex items-center px-3 py-1.5 bg-gray-900 text-white text-sm font-semibold rounded-full">
              Önerilen: {tl(butce)}
            </span>
          )}
          {fark != null && (
            <span className={`inline-flex items-center px-2.5 py-1 text-xs font-semibold rounded-full border ${fark >= 0 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
              Ağustos'a göre {yuzde(fark)}
            </span>
          )}
          <button onClick={() => window.print()} className="px-3 py-1.5 text-xs font-medium rounded-full border border-gray-300 text-gray-600 hover:bg-gray-50">
            Yazdır / PDF
          </button>
        </div>
      </div>
      {children}
    </div>
  );
}

export function Slayt({ no, toplam = 3, etiket, baslik, spot, accent = '#111827', children, dipnot }) {
  return (
    <section
      className="bg-white rounded-2xl border shadow-sm overflow-hidden print:rounded-none print:border-0 print:shadow-none"
      style={{ breakAfter: 'page', pageBreakAfter: 'always' }}
    >
      <div className="px-6 sm:px-8 pt-7 pb-5 border-b" style={{ borderColor: '#f1f5f9' }}>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            {etiket && (
              <span className="inline-block text-[10px] font-bold uppercase tracking-[0.14em] mb-2" style={{ color: accent }}>
                {etiket}
              </span>
            )}
            <h2 className="text-[22px] sm:text-[27px] font-bold text-gray-900 leading-[1.2] tracking-tight">{baslik}</h2>
            {spot && <p className="mt-2.5 text-sm text-gray-600 leading-relaxed max-w-3xl">{spot}</p>}
          </div>
          <span className="shrink-0 text-[11px] font-semibold text-gray-300 tabular-nums pt-1">{no} / {toplam}</span>
        </div>
      </div>
      <div className="px-6 sm:px-8 py-6 space-y-5">{children}</div>
      {dipnot && (
        <div className="px-6 sm:px-8 py-3 bg-gray-50 border-t text-[11px] text-gray-500 leading-relaxed">{dipnot}</div>
      )}
    </section>
  );
}

/** Büyük tek rakam. ton: 'iyi' | 'kotu' | 'notr' */
export function Metrik({ etiket, deger, alt, ton = 'notr' }) {
  const renk = ton === 'iyi' ? 'text-emerald-600' : ton === 'kotu' ? 'text-rose-600' : 'text-gray-900';
  return (
    <div className="rounded-xl border border-gray-200 bg-white px-4 py-3.5">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-1.5 leading-tight">{etiket}</div>
      <div className={`text-[26px] font-bold leading-none tabular-nums ${renk}`}>{deger}</div>
      {alt && <div className="mt-1.5 text-[11px] text-gray-500 leading-snug">{alt}</div>}
    </div>
  );
}

export function MetrikSira({ children, kolon = 4 }) {
  const g = { 2: 'sm:grid-cols-2', 3: 'sm:grid-cols-3', 4: 'sm:grid-cols-2 lg:grid-cols-4' }[kolon] || 'sm:grid-cols-4';
  return <div className={`grid grid-cols-1 gap-3 ${g}`}>{children}</div>;
}

/**
 * satirlar: [[hucre, hucre, ...], ...]
 * Hücre ya düz değer ya { v, ton, kalin } — ton: 'iyi' | 'kotu' | 'soluk'
 * saga: sağa hizalanacak kolon indeksleri
 * sarmala: metni satır sonuna sığmayınca ALTA KAYDIRACAK kolon indeksleri.
 *   ⚠️ Varsayılan whitespace-nowrap'tir; uzun açıklama kolonunu buraya yazmazsan
 *   tablo yatay kayar ve metin görünmeden kesilir.
 */
export function Tablo({ baslik, basliklar, satirlar, saga = [], sarmala = [], vurguSonSatir = false }) {
  const ton = (t) => (t === 'iyi' ? 'text-emerald-600' : t === 'kotu' ? 'text-rose-600' : t === 'soluk' ? 'text-gray-400' : 'text-gray-700');
  return (
    <div className="rounded-xl border border-gray-200 overflow-hidden">
      {baslik && <div className="bg-gray-50 px-4 py-2.5 text-[11px] font-semibold text-gray-700 border-b">{baslik}</div>}
      <div className="overflow-x-auto">
        <table className="min-w-full text-[11.5px]">
          <thead className="bg-white text-gray-400 border-b">
            <tr>
              {basliklar.map((h, i) => (
                <th key={i} className={`px-4 py-2 font-medium uppercase tracking-wider whitespace-nowrap ${saga.includes(i) ? 'text-right' : 'text-left'} ${sarmala.includes(i) ? 'min-w-[280px]' : ''}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {satirlar.map((r, ri) => {
              const son = vurguSonSatir && ri === satirlar.length - 1;
              return (
                <tr key={ri} className={son ? 'bg-gray-50 font-semibold' : 'bg-white'}>
                  {r.map((c, ci) => {
                    const o = c && typeof c === 'object' && !React.isValidElement(c) ? c : { v: c };
                    return (
                      <td key={ci} className={`px-4 py-2 ${sarmala.includes(ci) ? 'whitespace-normal min-w-[280px] leading-snug' : 'whitespace-nowrap'} ${saga.includes(ci) ? 'text-right tabular-nums' : ''} ${o.kalin || son ? 'font-semibold' : ''} ${ton(o.ton)}`}>
                        {o.v}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** tip: 'bilgi' | 'uyari' | 'riskli' | 'iyi' */
export function Not({ tip = 'bilgi', baslik, children }) {
  const s = {
    bilgi: 'bg-sky-50 border-sky-200 text-sky-900',
    uyari: 'bg-amber-50 border-amber-200 text-amber-900',
    riskli: 'bg-rose-50 border-rose-200 text-rose-900',
    iyi: 'bg-emerald-50 border-emerald-200 text-emerald-900',
  }[tip];
  return (
    <div className={`rounded-xl border px-4 py-3 ${s}`}>
      {baslik && <div className="text-[11px] font-bold mb-1">{baslik}</div>}
      <div className="text-[11.5px] leading-relaxed">{children}</div>
    </div>
  );
}

/** Bütçe dağılım çubuğu */
export function Dagilim({ kalemler, toplam }) {
  return (
    <div>
      <div className="flex h-9 rounded-lg overflow-hidden border border-gray-200">
        {kalemler.map((k, i) => (
          <div key={i} className="flex items-center justify-center text-[10px] font-semibold text-white"
               style={{ width: `${(k.tutar / toplam) * 100}%`, background: k.renk }} title={`${k.ad} ${tl(k.tutar)}`}>
            {k.tutar / toplam > 0.11 ? `${Math.round((k.tutar / toplam) * 100)}%` : ''}
          </div>
        ))}
      </div>
      <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
        {kalemler.map((k, i) => (
          <div key={i} className="flex items-start gap-2">
            <span className="mt-1 h-2.5 w-2.5 rounded-sm shrink-0" style={{ background: k.renk }} />
            <div className="min-w-0">
              <div className="text-[11px] font-semibold text-gray-800 leading-tight">{k.ad}</div>
              <div className="text-[11px] text-gray-500 tabular-nums">{tl(k.tutar)}</div>
              {k.not && <div className="text-[10px] text-gray-400 leading-tight mt-0.5">{k.not}</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Grup bağlamı — dört marka tek şirket, her sayfanın son slaytında aynı tablo. */
export function GrupSeridi({ vurgu }) {
  const satir = [
    { ad: 'HUPALUPA Store', agu: 449392, eyl: 495000 },
    { ad: 'HUPALUPA Eğlence', agu: 286044, eyl: 250000 },
    { ad: 'Kixx Sports Club', agu: 59219, eyl: 150000 },
    { ad: 'Keramet Ilıcası', agu: 189341, eyl: 120000 },
  ];
  const tAgu = satir.reduce((a, b) => a + b.agu, 0);
  const tEyl = satir.reduce((a, b) => a + b.eyl, 0);
  return (
    <div className="rounded-xl border border-gray-200 overflow-hidden">
      <div className="bg-gray-50 px-4 py-2.5 text-[11px] font-semibold text-gray-700 border-b">
        Grup bağlamı — dört marka tek şirket
      </div>
      <table className="min-w-full text-[11.5px]">
        <tbody className="divide-y divide-gray-100">
          {satir.map((s) => (
            <tr key={s.ad} className={s.ad === vurgu ? 'bg-amber-50' : 'bg-white'}>
              <td className={`px-4 py-2 ${s.ad === vurgu ? 'font-bold text-gray-900' : 'text-gray-600'}`}>{s.ad}</td>
              <td className="px-4 py-2 text-right tabular-nums text-gray-500">{tl(s.agu)}</td>
              <td className="px-4 py-2 text-right tabular-nums text-gray-400">→</td>
              <td className={`px-4 py-2 text-right tabular-nums ${s.ad === vurgu ? 'font-bold text-gray-900' : 'text-gray-700'}`}>{tl(s.eyl)}</td>
              <td className={`px-4 py-2 text-right tabular-nums text-[11px] ${s.eyl >= s.agu ? 'text-emerald-600' : 'text-amber-600'}`}>
                {yuzde((s.eyl - s.agu) / s.agu)}
              </td>
            </tr>
          ))}
          <tr className="bg-gray-50 font-semibold text-gray-900">
            <td className="px-4 py-2">Grup toplamı</td>
            <td className="px-4 py-2 text-right tabular-nums">{tl(tAgu)}</td>
            <td className="px-4 py-2 text-right text-gray-400">→</td>
            <td className="px-4 py-2 text-right tabular-nums">{tl(tEyl)}</td>
            <td className="px-4 py-2 text-right tabular-nums text-emerald-600">{yuzde((tEyl - tAgu) / tAgu)}</td>
          </tr>
        </tbody>
      </table>
      <div className="px-4 py-2.5 bg-white border-t text-[11px] text-gray-500 leading-relaxed">
        Kixx'in artışının büyük kısmı Eğlence ve Keramet'in <strong>sezonluk düşüşünden</strong> karşılanıyor:
        o iki markada talep Eylül'de gerçekten geriliyor. Grup toplamı {yuzde((tEyl - tAgu) / tAgu)} değişiyor.
      </div>
    </div>
  );
}

/* ── biçimleyiciler ─────────────────────────────────────────────── */
export function tl(n) {
  if (n == null) return '—';
  return '₺' + Math.round(n).toLocaleString('tr-TR');
}
export function sayi(n) {
  if (n == null) return '—';
  return Number(n).toLocaleString('tr-TR');
}
export function yuzde(x, basamak = 0) {
  if (x == null) return '—';
  const v = x * 100;
  return (v >= 0 ? '+' : '') + v.toFixed(basamak).replace('.', ',') + '%';
}
